// Commission (non-employee) API client. Same auth pattern as the payroll client
// (own axios instance, httpOnly session cookie via attachCookieAuth, never nukes
// the session on error).
import axios from 'axios';
import { apiConfig } from '@/config/api.config';
import { attachCookieAuth } from '@/lib/session';

const http = axios.create({ baseURL: apiConfig.baseURL, timeout: 20000, withCredentials: true });
http.interceptors.request.use(attachCookieAuth);

export interface CommissionLine {
  line_id: string;
  payee_id: string;
  payee_name?: string;
  plan_name?: string;
  activity_id: string;
  currency: string;
  eligible_amount: string;
  gross_commission: string;
  wht_category: string;
  wht_rate: string;
  wht_amount: string;
  net_amount: string;
  status: string;
  provider_ref: string;
  explanation?: string[];
  treaty_allowed?: boolean;
  treaty_claim_id?: string;
  tax_point_basis?: string;
  tax_point_date?: string;
  rule_code?: string;
}

export interface CommissionPayee {
  payee_id: string;
  legal_name: string;
  worker_category: string;
  country_code: string;
  status: string;
  agreement_signed: boolean;
  payout_channel: string;
  payout_provider: string;
}

export interface CommissionPlan {
  plan_id: string;
  version: number;
}

export interface TaxPreview {
  treatment: string;
  rule_code: string;
  rule_version: number;
  currency: string;
  eligible_amount: string;
  gross_commission: string;
  wht_rate: string;
  wht_amount: string;
  net_amount: string;
  finality: string;
  treaty_allowed: boolean;
  residency: string;
  explanation?: string[];
}

export interface ClassificationFactor {
  key: string;
  question: string;
  employee_label: string;
  independent_label: string;
}

export interface ClassificationResult {
  assessment_id: string;
  suggested_outcome: 'likely_employee' | 'likely_independent' | 'requires_review';
  rationale: string;
  employee_count: number;
  independent_count: number;
  answered: number;
}

export interface PayeeClassification {
  assessment_id?: string;
  suggested_outcome?: string;
  confirmed_outcome?: string;
  confirmed_reason?: string;
  answers?: Record<string, string>;
}

export const commissionApi = {
  async listLines(): Promise<CommissionLine[]> {
    const { data } = await http.get('/api/v1/commission/lines');
    return data.lines ?? [];
  },
  async listPayees(): Promise<CommissionPayee[]> {
    const { data } = await http.get('/api/v1/commission/payees');
    return data.payees ?? [];
  },
  async getLine(id: string): Promise<CommissionLine> {
    const { data } = await http.get(`/api/v1/commission/lines/${id}`);
    return data;
  },
  async createPayee(body: Record<string, unknown>): Promise<{ payee_id: string }> {
    const { data } = await http.post('/api/v1/commission/payees', body);
    return data;
  },
  async createPlan(body: Record<string, unknown>): Promise<CommissionPlan> {
    const { data } = await http.post('/api/v1/commission/plans', body);
    return data;
  },
  async recordActivity(body: Record<string, unknown>): Promise<{ activity_id: string; created: boolean }> {
    const { data } = await http.post('/api/v1/commission/activities', body);
    return data;
  },
  async calculate(activityId: string): Promise<CommissionLine> {
    const { data } = await http.post(`/api/v1/commission/activities/${activityId}/calculate`);
    return data;
  },
  // Live, non-persisted tax preview for the payment-entry screen (raw inputs).
  async preview(body: {
    country_code: string; worker_category: string; entity_type?: string; residency?: string;
    payment_classification?: string; plan_type: string; percent_rate?: string; flat_amount?: string;
    eligible_amount: string; currency?: string; event_date?: string;
  }): Promise<TaxPreview> {
    const { data } = await http.post('/api/v1/commission/preview', body);
    return data;
  },
  // Apply a tax-treaty override to a line (reason + evidence mandatory).
  async treatyOverride(lineId: string, body: { rate: string; reason: string; evidence_doc: string }): Promise<CommissionLine> {
    const { data } = await http.post(`/api/v1/commission/lines/${lineId}/treaty-override`, body);
    return data;
  },
  async approve(lineId: string): Promise<CommissionLine> {
    const { data } = await http.post(`/api/v1/commission/lines/${lineId}/approve`);
    return data;
  },
  async payout(lineId: string): Promise<CommissionLine> {
    const { data } = await http.post(`/api/v1/commission/lines/${lineId}/payout`);
    return data;
  },
  async getFactors(): Promise<ClassificationFactor[]> {
    const { data } = await http.get('/api/v1/commission/classification/factors');
    return data.factors ?? [];
  },
  async getClassification(payeeId: string): Promise<PayeeClassification | null> {
    try {
      const { data } = await http.get(`/api/v1/commission/payees/${payeeId}/classification`);
      return data;
    } catch {
      return null;
    }
  },
  async assess(payeeId: string, answers: Record<string, string>): Promise<ClassificationResult> {
    const { data } = await http.post(`/api/v1/commission/payees/${payeeId}/assess`, { answers });
    return data;
  },
  async confirmClassification(assessmentId: string, outcome: string, reason: string): Promise<void> {
    await http.post(`/api/v1/commission/classification/${assessmentId}/confirm`, { outcome, reason });
  },
  async downloadStatement(lineId: string, filename: string): Promise<void> {
    await downloadPdf(`/api/v1/commission/lines/${lineId}/statement.pdf`, filename);
  },
  async downloadCertificate(lineId: string, filename: string): Promise<void> {
    await downloadPdf(`/api/v1/commission/lines/${lineId}/wht-certificate.pdf`, filename);
  },
  // Batched Contractor & Commission pay run.
  async listRuns(): Promise<CommissionRun[]> {
    const { data } = await http.get('/api/v1/commission/runs');
    return data.runs ?? [];
  },
  async createRun(body: { name: string; country_code: string; currency: string; period_start?: string; period_end?: string }): Promise<CommissionRun> {
    const { data } = await http.post('/api/v1/commission/runs', body);
    return data;
  },
  // lines_withheld: the run's company is outside the caller's pay-details
  // reach, so its per-person fee lines are not sent (ME7 Part C).
  async getRun(id: string): Promise<{ run: CommissionRun; lines: CommissionLine[]; lines_withheld?: boolean }> {
    const { data } = await http.get(`/api/v1/commission/runs/${id}`);
    return data;
  },
  async attachLines(id: string, lineIds: string[]): Promise<{ attached: number }> {
    const { data } = await http.post(`/api/v1/commission/runs/${id}/attach`, { line_ids: lineIds });
    return data;
  },
  async runAction(id: string, action: 'validate' | 'approve' | 'lock' | 'pay' | 'reconcile'): Promise<CommissionRun> {
    const { data } = await http.post(`/api/v1/commission/runs/${id}/${action}`);
    return data;
  },
  async voidRun(id: string, reason: string): Promise<CommissionRun> {
    const { data } = await http.post(`/api/v1/commission/runs/${id}/void`, { reason });
    return data;
  },
  async getRunGL(id: string): Promise<GLJournal> {
    const { data } = await http.get(`/api/v1/commission/runs/${id}/gl`);
    return data;
  },
  // WHT remittance register.
  async listRemittances(): Promise<WHTRemittance[]> {
    const { data } = await http.get('/api/v1/wht/remittances');
    return data.remittances ?? [];
  },
  async buildRemittance(body: { country_code: string; tax_code: string; filing_period: string }): Promise<WHTRemittance> {
    const { data } = await http.post('/api/v1/wht/remittances/build', body);
    return data;
  },
  async markRemittance(id: string, body: { status: 'remitted' | 'filed' | 'reconciled' | 'reversed'; certificate_no?: string; evidence_doc: string; authority_reference?: string; remittance_date?: string }): Promise<WHTRemittance> {
    const { data } = await http.post(`/api/v1/wht/remittances/${id}/mark`, body);
    return data;
  },
  // Treaty claims (dated, evidence-backed, SoD).
  async listTreatyClaims(payeeId?: string): Promise<TreatyClaim[]> {
    const { data } = await http.get('/api/v1/commission/treaty-claims', { params: payeeId ? { payee_id: payeeId } : {} });
    return data.claims ?? [];
  },
  async createTreatyClaim(body: Record<string, unknown>): Promise<TreatyClaim> {
    const { data } = await http.post('/api/v1/commission/treaty-claims', body);
    return data;
  },
  async treatyClaimAction(id: string, action: 'approve' | 'reject'): Promise<TreatyClaim> {
    const { data } = await http.post(`/api/v1/commission/treaty-claims/${id}/${action}`);
    return data;
  },
  async applyTreatyClaim(lineId: string, claimId: string): Promise<CommissionLine> {
    const { data } = await http.post(`/api/v1/commission/lines/${lineId}/apply-treaty-claim`, { claim_id: claimId });
    return data;
  },
};

export interface TreatyClaim {
  id: string;
  payee_id: string;
  treaty_country: string;
  payment_classification: string;
  treaty_article: string;
  approved_rate: string;
  valid_from: string;
  valid_to: string;
  certificate_ref: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
}

export interface CommissionRun {
  id: string;
  name: string;
  country_code: string;
  currency: string;
  period_start: string;
  period_end: string;
  status: string;
  line_count: number;
  gross_total: string;
  wht_total: string;
  net_total: string;
  void_reason: string;
  /** Multi-entity Phase 5: the paying company. */
  legal_entity_id?: string | null;
}

export interface GLJournal {
  currency: string;
  balanced: boolean;
  total_dr: string;
  total_cr: string;
  entries: { account_code: string; account: string; dr_cr: string; amount: string }[];
}

export interface WHTRemittance {
  id: string;
  country_code: string;
  tax_code: string;
  filing_period: string;
  gross_total: string;
  wht_total: string;
  line_count: number;
  status: 'pending' | 'remitted' | 'filed';
  certificate_no: string;
  evidence_doc: string;
  remitted_at: string | null;
}

async function downloadPdf(path: string, filename: string): Promise<void> {
  const res = await http.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
